/**
 * 全局背景音乐管理器
 * 负责管理整个应用的背景音乐播放
 */

import { userSettingsApi } from '@/api/userSettings';

class BackgroundMusicManager {
  constructor() {
    this.audio = null;
    this.isPlaying = false;
    this.isEnabled = true;
    this.volume = 0.3; // 30% 音量
    this.currentMusic = null;
    this.userId = null;
    
    // 音乐文件列表 - 本地音乐文件为默认
    this.musicFiles = [
      require('@/assets/media/bgm_audio.mp3'), // 本地背景音乐文件
      'https://music.163.com/song/media/outer/url?id=123456', // 示例：网易云音乐
      'https://www.bensound.com/bensound-music/bensound-sunny.mp3', // 示例：免费音乐
      'https://www.soundjay.com/misc/sounds/bell-ringing-05.wav' // 示例：免费音效
    ];
    
    this.init();
  }

  /**
   * 初始化背景音乐管理器
   */
  async init() {
    try {
      // 获取用户ID
      const userInfoStr = localStorage.getItem("userInfo");
      if (userInfoStr) {
        const userInfo = JSON.parse(userInfoStr);
        this.userId = userInfo.id;
      }

      // 加载用户设置
      await this.loadUserSettings();
      
      // 不自动创建音频对象，等待手动触发
      console.log('🎵 背景音乐管理器初始化完成（等待手动触发）');
    } catch (error) {
      console.error('背景音乐管理器初始化失败:', error);
    }
  }

  /**
   * 加载用户设置
   */
  async loadUserSettings() {
    try {
      if (!this.userId) return;
      
      // getUserSettings(category) -> 我们未分类请求，拿到的是对象 { basic: [...], advanced: [...], ... }
      const settingsData = await userSettingsApi.getUserSettings();
      // 扁平化所有分类数组
      const flatSettings = Object.values(settingsData)
        .filter(Array.isArray)
        .reduce((acc, arr) => acc.concat(arr), []);

      const audioSetting = flatSettings.find(s => s.key === 'auto_play_audio' || s.setting_key === 'auto_play_audio');
      const musicUrlSetting = flatSettings.find(s => s.key === 'background_music_url' || s.setting_key === 'background_music_url');
      
      if (audioSetting) {
        this.isEnabled = audioSetting.setting_value === '1';
        console.log('🎵 背景音乐设置:', this.isEnabled ? '开启' : '关闭');
      }
      
      // 如果用户设置了自定义音乐URL，使用用户设置的URL
      if (musicUrlSetting && musicUrlSetting.setting_value) {
        this.musicFiles = [musicUrlSetting.setting_value];
        console.log('🎵 使用用户自定义音乐URL:', musicUrlSetting.setting_value);
      }
    } catch (error) {
      console.error('加载用户设置失败:', error);
      // 默认开启
      this.isEnabled = true;
    }
  }

  /**
   * 创建音频对象
   */
  async createAudio() {
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }

    // 获取音乐URL
    const musicUrl = await this.getMusicUrl();
    
    this.audio = new Audio(musicUrl);
    this.audio.loop = true;
    this.audio.volume = this.volume;
    this.currentMusic = musicUrl;

    // 添加事件监听
    this.audio.addEventListener('loadstart', () => {
      console.log('🎵 开始加载背景音乐');
    });

    this.audio.addEventListener('canplay', () => {
      console.log('🎵 背景音乐加载完成');
      if (this.isEnabled && !this.isPlaying) {
        this.play();
      }
    });

    this.audio.addEventListener('error', () => {
      console.error('🎵 背景音乐加载失败');
      // 尝试加载备用音乐
      this.loadFallbackMusic();
    });

    this.audio.addEventListener('ended', () => {
      console.log('🎵 背景音乐播放结束');
    });

    // 预加载音频
    this.audio.load();
  }

  /**
   * 获取音乐URL
   */
  async getMusicUrl() {
    try {
      // 1. 优先从用户设置获取
      if (this.userId) {
        const settingsData = await userSettingsApi.getUserSettings();
        const flatSettings = Object.values(settingsData)
          .filter(Array.isArray)
          .reduce((acc, arr) => acc.concat(arr), []);
        const musicUrlSetting = flatSettings.find(s => s.key === 'background_music_url' || s.setting_key === 'background_music_url');
        
        if (musicUrlSetting && musicUrlSetting.setting_value) {
          console.log('🎵 使用用户自定义音乐URL:', musicUrlSetting.setting_value);
          return musicUrlSetting.setting_value;
        }
      }
      
      // 2. 从API获取音乐URL
      const apiUrl = await this.getMusicUrlFromAPI();
      if (apiUrl) {
        console.log('🎵 使用API获取的音乐URL:', apiUrl);
        return apiUrl;
      }
      
      // 3. 使用默认音乐URL列表
      const defaultUrl = this.musicFiles[0];
      console.log('🎵 使用默认音乐URL:', defaultUrl);
      return defaultUrl;
      
    } catch (error) {
      console.error('获取音乐URL失败:', error);
      // 返回默认URL
      return this.musicFiles[0];
    }
  }

  /**
   * 从API获取音乐URL
   */
  async getMusicUrlFromAPI() {
    try {
      // 这里可以调用后端API获取音乐URL
      // 例如：从音乐库API获取随机音乐
      const response = await fetch('/api/music/getRandomMusic', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userId: this.userId,
          category: 'background'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        return data.musicUrl;
      }
    } catch (error) {
      console.error('从API获取音乐URL失败:', error);
    }
    return null;
  }

  /**
   * 加载备用音乐
   */
  async loadFallbackMusic() {
    if (this.musicFiles.length > 1) {
      const fallbackUrl = this.musicFiles[1];
      console.log('🎵 尝试加载备用音乐:', fallbackUrl);
      
      this.audio.src = fallbackUrl;
      this.currentMusic = fallbackUrl;
      this.audio.load();
    } else {
      // 如果没有备用音乐，尝试重新获取音乐URL
      try {
        const newUrl = await this.getMusicUrlFromAPI();
        if (newUrl) {
          console.log('🎵 尝试加载API备用音乐:', newUrl);
          this.audio.src = newUrl;
          this.currentMusic = newUrl;
          this.audio.load();
        }
      } catch (error) {
        console.error('加载备用音乐失败:', error);
      }
    }
  }

  /**
   * 播放背景音乐
   */
  async play() {
    if (!this.audio || !this.isEnabled) return;

    try {
      await this.audio.play();
      this.isPlaying = true;
      console.log('🎵 背景音乐开始播放');
    } catch (error) {
      console.error('🎵 背景音乐播放失败:', error);
      // 浏览器可能阻止了自动播放，等待用户交互
      this.waitForUserInteraction();
    }
  }

  /**
   * 暂停背景音乐
   */
  pause() {
    if (!this.audio) return;

    this.audio.pause();
    this.isPlaying = false;
    console.log('🎵 背景音乐已暂停');
  }

  /**
   * 停止背景音乐
   */
  stop() {
    if (!this.audio) return;

    this.audio.pause();
    this.audio.currentTime = 0;
    this.isPlaying = false;
    console.log('🎵 背景音乐已停止');
  }

  /**
   * 设置音量
   */
  setVolume(volume) {
    this.volume = Math.max(0, Math.min(1, volume));
    if (this.audio) {
      this.audio.volume = this.volume;
    }
    console.log('🎵 背景音乐音量设置为:', Math.round(this.volume * 100) + '%');
  }

  /**
   * 启用/禁用背景音乐
   */
  async setEnabled(enabled) {
    this.isEnabled = enabled;
    
    if (enabled) {
      await this.play();
    } else {
      this.pause();
    }

    // 保存设置到服务器
    if (this.userId) {
      try {
  // 现在的单项更新只需要 key/value，不再传 userId
  await userSettingsApi.updateUserSetting('auto_play_audio', enabled ? '1' : '0');
        console.log('🎵 背景音乐设置已保存到服务器');
      } catch (error) {
        console.error('保存背景音乐设置失败:', error);
      }
    }
  }

  /**
   * 等待用户交互后播放
   */
  waitForUserInteraction() {
    const playOnInteraction = async () => {
      try {
        await this.play();
        // 移除事件监听器
        document.removeEventListener('click', playOnInteraction);
        document.removeEventListener('keydown', playOnInteraction);
        document.removeEventListener('touchstart', playOnInteraction);
      } catch (error) {
        console.error('用户交互后播放失败:', error);
      }
    };

    // 添加事件监听器
    document.addEventListener('click', playOnInteraction, { once: true });
    document.addEventListener('keydown', playOnInteraction, { once: true });
    document.addEventListener('touchstart', playOnInteraction, { once: true });
    
    console.log('🎵 等待用户交互后播放背景音乐');
  }

  /**
   * 切换背景音乐
   */
  toggle() {
    if (this.isPlaying) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * 手动启动背景音乐（用于强制复习弹窗）
   */
  async startForcedReviewMusic() {
    try {
      console.log('🎵 强制复习模式：启动背景音乐');
      
      // 创建音频对象
      await this.createAudio();
      
      // 等待音频加载完成后播放
      return new Promise((resolve) => {
        if (this.audio) {
          this.audio.addEventListener('canplay', async () => {
            try {
              await this.play();
              console.log('🎵 强制复习背景音乐开始播放');
              resolve(true);
            } catch (error) {
              console.error('🎵 强制复习背景音乐播放失败:', error);
              resolve(false);
            }
          }, { once: true });
        } else {
          resolve(false);
        }
      });
    } catch (error) {
      console.error('🎵 启动强制复习背景音乐失败:', error);
      return false;
    }
  }

  /**
   * 停止强制复习背景音乐
   */
  stopForcedReviewMusic() {
    console.log('🎵 强制复习模式：停止背景音乐');
    this.pause();
  }


  /**
   * 获取当前状态
   */
  getStatus() {
    return {
      isPlaying: this.isPlaying,
      isEnabled: this.isEnabled,
      volume: this.volume,
      currentMusic: this.currentMusic
    };
  }

  /**
   * 销毁管理器
   */
  destroy() {
    if (this.audio) {
      this.audio.pause();
      this.audio = null;
    }
    this.isPlaying = false;
    console.log('🎵 背景音乐管理器已销毁');
  }
}

// 创建全局实例
const backgroundMusicManager = new BackgroundMusicManager();

export default backgroundMusicManager;
